import {
  ActionableDataTable,
  ActionableDataTableActionTypes,
} from '../common/table/ActionableDataTable';
import { humanReadableCriteria } from '../SweepFieldsOperatorSelector/sweepFieldsTypesAndOperators';
import { DataTableOnOrderChangeClbk } from '../common/table/TableTypes';
import useCrmOrgPermissions from '../common/permissions/useCrmOrgPermissions';
import { useSelector } from 'react-redux';
import { useTerritoriesActions } from '../../hooks/useTerritoriesActions';
import { selectTerritories } from '../../reducers/territoriesReducer';
import { useRunOnce } from '../common/useRunOnce';
import { Permission } from '@server/permissions';
import { useSweepFieldsInfo } from '../../sweep-fields/useSweepFieldsInfo';
import { DataTableId } from '../common/table/utils';
import { useGetNameFromAssignTo } from '../pages/funnel-map-flow/dialogs/stage/useGetNameFromAssignTo';

interface AssignmentRulesTableProps {
  assignmentRules: AutomationAssignmentRule[];
  onAssignmentRulesChange: (assignmentRules: AutomationAssignmentRule[]) => any;
  onAssignmentRuleEdit: (assignmentRuleId: string) => any;
  onAssignmentRuleDuplicate: (assignmentRuleId: string) => any;
  readonly?: boolean;
}

const columns = [
  {
    field: 'priority',
    headerName: 'Priority',
    width: '78px',
    minWidth: '98px',
  },
  { field: 'name', headerName: 'Name', minWidth: '200px' },
  { field: 'rule', headerName: 'Rule', minWidth: '300px' },
  { field: 'assignedTo', headerName: 'Assigned to' },
];

interface AssignmentsDataRow {
  id: string;
  priority: string;
  name: string;
  rule: string;
  assignedTo: string;
}

export const AssignmentRulesTable = ({
  assignmentRules,
  onAssignmentRulesChange,
  onAssignmentRuleEdit,
  onAssignmentRuleDuplicate,
  readonly,
}: AssignmentRulesTableProps) => {
  const permissionString: Permission[] = ['edit:funnels'];
  const [isAllowedBtn] = useCrmOrgPermissions(permissionString);

  const territories = useSelector(selectTerritories);
  const { getTerritories } = useTerritoriesActions();
  const { enrichCriteriaWithLabelsAndSfdcDisplayType } = useSweepFieldsInfo();
  const { getNameFromAssignTo } = useGetNameFromAssignTo();

  useRunOnce(async () => {
    await getTerritories();
  });

  const rows: AssignmentsDataRow[] = assignmentRules.map((rule, idx) => ({
    id: rule._id,
    priority: '' + (idx + 1),
    assignedTo: getNameFromAssignTo(rule.assignedTo),
    name: rule.name,
    rule: humanReadableCriteria(
      rule.when?.criteria?.criteria.length
        ? rule.when?.criteria?.criteria.map((c) => enrichCriteriaWithLabelsAndSfdcDisplayType(c))
        : [],
      rule.when?.criteria?.criteriaLogic,
      territories,
    ),
  }));

  const onOrderChange: DataTableOnOrderChangeClbk = ({ sourceIndex, destinationIndex }) => {
    const newAssignments = [...assignmentRules];
    newAssignments.splice(sourceIndex, 1);
    newAssignments.splice(destinationIndex, 0, assignmentRules[sourceIndex]);
    onAssignmentRulesChange(newAssignments);
  };
  const getActions =
    !isAllowedBtn || readonly
      ? []
      : [
          { type: ActionableDataTableActionTypes.EDIT },
          {
            type: ActionableDataTableActionTypes.DUPLICATE,
          },
          {
            type: ActionableDataTableActionTypes.DELETE,
            confirm: true,
            confirmButtonOkLabel: 'Yes, Delete',
            confirmButtonCancelLabel: 'Cancel',
            confirmTitle: 'Delete Rule',
            confirmSubtitle: 'Are you sure you want to delete this rule?',
          },
        ];
  return (
    <ActionableDataTable
      tableId={DataTableId.AssignmentRulesTable}
      allowRowReorder={isAllowedBtn && !readonly}
      columns={columns}
      rows={rows}
      {...(isAllowedBtn && !readonly ? { onRowOrderChange: onOrderChange } : {})}
      actions={getActions}
      onActionClick={({ actionType, row }) => {
        switch (actionType) {
          case ActionableDataTableActionTypes.DELETE:
            const newAssignmentRules = assignmentRules.filter((r) => r._id !== row.id);
            onAssignmentRulesChange(newAssignmentRules);
            break;
          case ActionableDataTableActionTypes.EDIT:
            onAssignmentRuleEdit(row.id);
            break;
          case ActionableDataTableActionTypes.DUPLICATE:
            onAssignmentRuleDuplicate(row.id);
            break;
        }
      }}
      onRowClick={(
        e: React.MouseEvent<HTMLTableCellElement, MouseEvent>,
        row: AssignmentsDataRow,
      ) => {
        onAssignmentRuleEdit(row.id);
      }}
    />
  );
};
